import React from 'react';
import { Badge, Card } from 'react-bootstrap';
import { formatTripWhen, shortAddress } from '../../Utils/bookingContext';
import { formatAmount } from '../../Utils/billingoDocuments';

// Status → variant, matching the Orders table's convention so a booking reads
// the same wherever it appears.
const STATUS_VARIANT = {
  Completed: 'success',
  Confirmed: 'primary',
  Cancelled: 'danger',
  Pending: 'warning',
};

function Field({ label, value, icon }) {
  if (!value) return null;
  return (
    <div className="d-flex gap-2 small">
      <span className="text-muted text-nowrap" style={{ minWidth: 78 }}>
        {icon && <i className={`fa ${icon} me-1 opacity-75`} />}
        {label}
      </span>
      <span className="text-break">{value}</span>
    </div>
  );
}

/**
 * The order behind an invoice, at a glance: who travelled, when, on which
 * route, for how much. Rendered wherever an agent is looking at a document
 * (details modal, final-Számla queue) so they never have to leave invoicing to
 * answer "what was this for?".
 */
function BookingContextCard({ booking, onOpenOrder = null, className = '' }) {
  if (!booking) return null;

  const route = [booking.from, booking.to].filter(Boolean).length > 0;

  return (
    <Card className={`border-0 bg-light-subtle ${className}`}>
      <Card.Body className="py-2 px-3">
        <div className="d-flex justify-content-between align-items-start gap-2 mb-1 flex-wrap">
          <div className="fw-semibold">
            {booking.booking_reference || booking.booking_id}
            {booking.customer_name && (
              <span className="text-muted fw-normal">
                {` · ${booking.customer_name}`}
              </span>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            {booking.status && (
              <Badge bg={STATUS_VARIANT[booking.status] || 'secondary'} className="fw-normal">
                {booking.status}
              </Badge>
            )}
            {onOpenOrder && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-decoration-none"
                onClick={() => onOpenOrder(booking)}
              >
                Open order
                <i className="fa fa-arrow-up-right-from-square ms-1" />
              </button>
            )}
          </div>
        </div>

        <Field label="When" icon="fa-clock" value={formatTripWhen(booking.date)} />
        {route && (
          <Field
            label="Route"
            icon="fa-route"
            value={(
              <>
                {shortAddress(booking.from, 60) || '—'}
                <i className="fa fa-arrow-right mx-2 text-muted small" />
                {shortAddress(booking.to, 60) || '—'}
              </>
            )}
          />
        )}
        <Field label="Return" icon="fa-rotate-left" value={formatTripWhen(booking.return_date)} />
        <Field
          label="Vehicle"
          icon="fa-car"
          value={[booking.category, booking.passengers ? `${booking.passengers} pax` : '']
            .filter(Boolean).join(' · ')}
        />
        <Field label="Flight" icon="fa-plane" value={booking.flight_number} />
        <Field label="Programme" icon="fa-map" value={booking.programme} />
        <Field label="Event" icon="fa-calendar-day" value={booking.event_name} />
        <Field label="Contact" icon="fa-envelope" value={booking.email_address} />
        <Field
          label="Order value"
          icon="fa-tag"
          value={booking.total_price != null
            ? formatAmount(booking.total_price, booking.currency)
            : ''}
        />
      </Card.Body>
    </Card>
  );
}

export default BookingContextCard;
